import { useEffect } from 'react';
import { Card, Tabs } from 'antd';
import { useSimulation } from '../../context/SimulationContext';
import { useArtifactTracking } from '../../hooks/useArtifactTracking';
import { ScenarioArtifact } from '../../types/simulation.types';
import JsonTableRenderer from './renderers/JsonTableRenderer';
import TextDocRenderer from './renderers/TextDocRenderer';
import CodeRenderer from './renderers/CodeRenderer';
import FormRenderer from './renderers/FormRenderer';
import TimelineArtifactRenderer from './renderers/TimelineArtifactRenderer';
import './ArtifactWorkspace.scss';

interface ArtifactWorkspaceProps {
  onTrackingReady: (tracking: ReturnType<typeof useArtifactTracking>) => void;
}

function ArtifactWorkspace({ onTrackingReady }: ArtifactWorkspaceProps) {
  const { artifacts, dispatch } = useSimulation();
  const tracking = useArtifactTracking(artifacts);

  useEffect(() => {
    onTrackingReady(tracking);
  }, [tracking, onTrackingReady]);

  if (artifacts.length === 0) return null;

  const items = artifacts.map((artifact) => ({
    key: artifact.artifactId,
    label: artifact.label,
    children: (
      <ArtifactRenderer
        artifact={artifact}
        onRead={() => tracking.trackRead(artifact.artifactId)}
        onChange={(newContent: unknown) => {
          tracking.trackWrite(artifact.artifactId);
          dispatch({
            type: 'UPDATE_ARTIFACT',
            payload: { artifactId: artifact.artifactId, content: newContent },
          });
        }}
      />
    ),
  }));

  return (
    <Card title="Workspace" className="artifact-workspace" size="small">
      <Tabs items={items} />
    </Card>
  );
}

function ArtifactRenderer({
  artifact,
  onRead,
  onChange,
}: {
  artifact: ScenarioArtifact;
  onRead: () => void;
  onChange: (newContent: unknown) => void;
}) {
  // Track read on first render of this artifact
  useEffect(() => {
    onRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  switch (artifact.artifactType) {
    case 'json_table':
      return (
        <JsonTableRenderer
          content={artifact.content as any}
          editable={artifact.editable}
          onChange={onChange}
        />
      );
    case 'text_doc':
      return (
        <TextDocRenderer
          content={artifact.content as string}
          editable={artifact.editable}
          onChange={onChange}
        />
      );
    case 'code':
      return (
        <CodeRenderer
          content={artifact.content as any}
          editable={artifact.editable}
          onChange={onChange}
        />
      );
    case 'form':
      return (
        <FormRenderer
          content={artifact.content as any}
          editable={artifact.editable}
          onChange={onChange}
        />
      );
    case 'timeline':
      return <TimelineArtifactRenderer content={artifact.content as any} />;
    default:
      return (
        <TextDocRenderer
          content={JSON.stringify(artifact.content, null, 2)}
          editable={false}
          onChange={onChange}
        />
      );
  }
}

export default ArtifactWorkspace;
